/**
 * PWA Install Prompt Component
 * Shows install banner and handles PWA installation
 */

import React, { useState, useEffect } from 'react';
import { safeGetItem, safeSetItem } from '../utils/safeStorage';
import { Download, X, Smartphone, Monitor, Bell } from 'lucide-react';
import logger from '../utils/logger';
import {
  isPushSupported,
  enablePushNotifications,
  isPushEnabled,
  initializePushNotifications,
  unsubscribeFromPush
} from '../services/pushNotifications';
import { apiClient } from '../services/api';

// BeforeInstallPromptEvent interface
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Detect device type
const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (/mobile|android|iphone|ipod|blackberry|windows phone/i.test(userAgent)) {
    return 'mobile';
  }
  if (/ipad|tablet|playbook|silk/i.test(userAgent)) {
    return 'tablet';
  }
  return 'desktop';
};

// Check if running as installed PWA
const isInstalledPWA = (): boolean => {
  return window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true;
};

export const PWAInstallPrompt: React.FC = () => {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [isInstalled, setIsInstalled] = useState(isInstalledPWA());
  const deviceType = getDeviceType();

  useEffect(() => {
    // Check if already dismissed
    const dismissed = safeGetItem('pwa-install-dismissed');
    const dismissedTime = dismissed ? parseInt(dismissed, 10) : 0;
    const oneWeek = 7 * 24 * 60 * 60 * 1000;
    const shouldShow = Date.now() - dismissedTime > oneWeek;

    // Listen for install prompt
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
      if (shouldShow && !isInstalled) {
        setShowBanner(true);
      }
    };

    // Listen for successful install
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowBanner(false);
      setInstallPrompt(null);
      // Show notification prompt after install
      setTimeout(() => {
        if (isPushSupported() && !notificationsEnabled) {
          setShowNotificationPrompt(true);
        }
      }, 2000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Initialize push notifications
    initializePushNotifications().then(result => {
      setNotificationsEnabled(result.permission === 'granted' && !!result.subscription);
    });

    // Check if push is enabled
    isPushEnabled().then(setNotificationsEnabled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isInstalled, notificationsEnabled]);

  const handleInstall = async () => {
    if (!installPrompt) return;

    try {
      await installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;

      if (outcome === 'accepted') {
        logger.log('PWA installed');
      } else {
        logger.log('PWA installation dismissed');
        safeSetItem('pwa-install-dismissed', Date.now().toString());
      }
    } catch (error) {
      logger.error('Install error:', error);
    }

    setShowBanner(false);
    setInstallPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    safeSetItem('pwa-install-dismissed', Date.now().toString());
  };

  const handleEnableNotifications = async () => {
    const result = await enablePushNotifications();
    setNotificationsEnabled(result.success);
    setShowNotificationPrompt(false);
  };

  const handleDismissNotifications = () => {
    setShowNotificationPrompt(false);
  };

  // iOS install instructions
  const isIOS = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
  const showIOSInstructions = isIOS && !isInstalled && showBanner;

  if (!showBanner && !showNotificationPrompt) return null;

  return (
    <>
      {/* Install Banner */}
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-gradient-to-r from-blue-600 to-blue-700 shadow-lg">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                {deviceType === 'mobile' ? (
                  <Smartphone className="w-6 h-6 text-white" />
                ) : (
                  <Monitor className="w-6 h-6 text-white" />
                )}
              </div>
              <div className="text-white">
                <h3 className="font-semibold text-lg">Install KELZIX App</h3>
                <p className="text-sm text-blue-100">
                  {showIOSInstructions
                    ? 'Tap the Share button, then "Add to Home Screen"'
                    : 'Install for quick access and offline monitoring'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!showIOSInstructions && (
                <button
                  onClick={handleInstall}
                  className="flex items-center gap-2 px-4 py-2 bg-white text-blue-600 rounded-lg font-medium hover:bg-blue-50 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Install
                </button>
              )}
              <button
                onClick={handleDismiss}
                className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                aria-label="Dismiss"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Permission Prompt */}
      {showNotificationPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-blue-100 rounded-full">
                <Bell className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <h3 className="text-xl font-semibold text-center text-gray-900 mb-2">
              Enable Alert Notifications
            </h3>
            <p className="text-center text-gray-600 mb-6">
              Get instant alerts when temperature or humidity goes out of range.
              Never miss a critical warning!
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDismissNotifications}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
              >
                Not Now
              </button>
              <button
                onClick={handleEnableNotifications}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <Bell className="w-4 h-4" />
                Enable
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Notification Settings Component (for Settings page)
export const NotificationSettings: React.FC = () => {
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [enabling, setEnabling] = useState(false);

  useEffect(() => {
    isPushEnabled().then(enabled => {
      setNotificationsEnabled(enabled);
      setLoading(false);
    });
  }, []);

  const handleToggle = async () => {
    if (notificationsEnabled) {
      // Unsubscribe from push notifications
      setEnabling(true);
      try {
        await unsubscribeFromPush();
        // Also remove server-side subscription record (best-effort)
        try {
          await apiClient.delete('/api/notifications/unsubscribe');
        } catch {
          // Server cleanup is best-effort — browser unsubscribe already succeeded
        }
        setNotificationsEnabled(false);
      } catch (error) {
        logger.error('Failed to unsubscribe from push:', error);
      }
      setEnabling(false);
      return;
    }

    setEnabling(true);
    const result = await enablePushNotifications();
    setNotificationsEnabled(result.success);
    setEnabling(false);
  };

  if (!isPushSupported()) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-yellow-800 text-sm">
          Push notifications are not supported in this browser.
        </p>
      </div>
    );
  }

  if (loading) {
    return <div className="animate-pulse h-12 bg-gray-200 rounded-lg" />;
  }

  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-3">
        <Bell className={`w-5 h-5 ${notificationsEnabled ? 'text-blue-600' : 'text-gray-400'}`} />
        <div>
          <p className="font-medium text-gray-900">Push Notifications</p>
          <p className="text-sm text-gray-500">
            {notificationsEnabled ? 'Receiving alerts on this device' : 'Enable to receive alert notifications'}
          </p>
        </div>
      </div>
      <button
        onClick={handleToggle}
        disabled={enabling}
        className={`
          relative inline-flex h-6 w-11 items-center rounded-full transition-colors
          ${notificationsEnabled ? 'bg-blue-600' : 'bg-gray-300'}
          ${enabling ? 'opacity-50 cursor-wait' : 'cursor-pointer'}
        `}
      >
        <span
          className={`
            inline-block h-4 w-4 transform rounded-full bg-white transition-transform
            ${notificationsEnabled ? 'translate-x-6' : 'translate-x-1'}
          `}
        />
      </button>
    </div>
  );
};

export default PWAInstallPrompt;
